import React, { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import axios from 'axios'
import { toast } from 'react-toastify'

interface Props {
  closeInstagramModal: () => void
}

const initialValues = {
  userId: '',
  accessToken: ''
}

const validationSchema = Yup.object().shape({
  userId: Yup.string().required('User ID is required'),
  accessToken: Yup.string().required('Access Token is required')
})

const InstagramModal: React.FC<Props> = ({ closeInstagramModal }) => {
  const [loadingOL, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [hasExistingConfig, setHasExistingConfig] = useState(false)
  const [verifyToken, setVerifyToken] = useState<string | null>(null)
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null)
  const [copiedToken, setCopiedToken] = useState(false)
  const [copiedUrl, setCopiedUrl] = useState(false)

  const fetchExistingConfig = async () => {
    try {
      const token = localStorage.getItem('auth-token')

      const response = await axios.get(
        `${process.env.REACT_APP_RESUME_PARSER_URL}/get-instagramConfig`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      if (response.data && response.data.config && response.data.config.length > 0) {
        const configData = response.data.config[0]
        setHasExistingConfig(true)

        setVerifyToken(configData.verifyToken || null)
        setWebhookUrl(configData.webhookUrl || null)

        formik.setFieldValue('userId', configData.pageId || '')
        formik.setFieldValue('accessToken', configData.accessToken || '')
        return configData
      } else {
        setHasExistingConfig(false)
        setVerifyToken(null)
        setWebhookUrl(null)
        return null
      }
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        toast.info(error.response.data?.message || 'Instagram configuration not found.')
      } else {
        toast.error('Failed to fetch Instagram configuration.')
      }

      console.error('Error fetching Instagram configuration:', error)
      setHasExistingConfig(false)
      return null
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true)

        const payload = {
          pageId: values.userId,
          accessToken: values.accessToken
          // bgAccountId: currentPlan?.bgAccountId // from context
        }

        const token = localStorage.getItem('auth-token')

        const endpoint = hasExistingConfig
          ? `${process.env.REACT_APP_RESUME_PARSER_URL}/update-instagram-config`
          : `${process.env.REACT_APP_RESUME_PARSER_URL}/save-insta-config`

        await axios.post(endpoint, payload, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        toast.success(
          `Instagram configuration ${hasExistingConfig ? 'updated' : 'saved'} successfully!`
        )

        await fetchExistingConfig()
      } catch (error) {
        console.error('Error saving Instagram configuration:', error)
        toast.error(`Failed to ${hasExistingConfig ? 'update' : 'save'} configuration.`)
      } finally {
        setLoading(false)
      }
    }
  })

  useEffect(() => {
    const loadConfig = async () => {
      setInitialLoading(true)
      await fetchExistingConfig()
      setInitialLoading(false)
    }
    loadConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderInput = (
    name: keyof typeof initialValues,
    label: string,
    type = 'text',
    disabled = false
  ) => (
    <>
      <div className='d-flex align-items-start mt-6 px-3 py-2'>
        <label className='fs-6 fw-bold mb-2'>{label}:</label>
      </div>
      <input
        placeholder={`Enter ${label.toLowerCase()}`}
        type={type}
        autoComplete='off'
        {...formik.getFieldProps(name)}
        className={clsx(
          'form-control form-control-lg',
          { 'is-invalid': formik.touched[name] && formik.errors[name] },
          { 'is-valid': formik.touched[name] && !formik.errors[name] }
        )}
        disabled={disabled}
      />
      {formik.touched[name] && formik.errors[name] && (
        <div className='d-flex align-items-start mt-1 px-3 py-2'>
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors[name]}</span>
            </div>
          </div>
        </div>
      )}
    </>
  )

  if (initialLoading) {
    return (
      <div className='card w-600px h-400px mb-4 p-2 position-relative text-center'>
        <button
          type='button'
          className='btn-close position-absolute top-0 end-0 m-n4 rounded-circle'
          aria-label='Close'
          onClick={closeInstagramModal}
          style={{
            backgroundColor: 'rgba(218, 222, 243)',
            height: '25px',
            width: '25px',
            color: 'rgba(52, 60, 106)'
          }}
        ></button>
        <div className='d-flex justify-content-center align-items-center h-100'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  const handleCopy = (textToCopy: string, type: 'token' | 'url') => {
    if (!textToCopy) return

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        const successMessage = type === 'token' ? 'Verify Token' : 'Callback URL'
        toast.success(`${successMessage} copied to clipboard!`)

        if (type === 'token') {
          setCopiedToken(true)
          setTimeout(() => setCopiedToken(false), 2000)
        } else {
          setCopiedUrl(true)
          setTimeout(() => setCopiedUrl(false), 2000)
        }
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err)
        toast.error('Failed to copy to clipboard.')
      })
  }

  return (
    <div className='card w-600px h-400px mb-4 p-2 position-relative text-center'>
      <button
        type='button'
        className='btn-close position-absolute top-0 end-0 m-n4 rounded-circle'
        aria-label='Close'
        onClick={closeInstagramModal}
        style={{
          backgroundColor: 'rgba(218, 222, 243)',
          height: '25px',
          width: '25px',
          color: 'rgba(52, 60, 106)'
        }}
      ></button>

      <div className='d-flex justify-content-between align-items-center px-3 py-2'>
        <h2 className='mb-0'>Instagram Configuration</h2>
      </div>
      <div className='d-flex justify-content-between align-items-center px-3 py-2'>
        <p className='mb-0'>
          {hasExistingConfig
            ? 'Update your Instagram configuration details below.'
            : 'Enter your User ID and Access Token to configure Instagram.'}
        </p>
      </div>

      <form
        onSubmit={formik.handleSubmit}
        className='d-flex flex-column mb-10 fv-row fv-plugins-icon-container'
      >
        {renderInput('userId', 'User ID')}
        {renderInput('accessToken', 'Access Token')}

        {verifyToken && webhookUrl && (
          <div className='px-3 py-2 mt-4'>
            <div className='p-4 border rounded bg-light'>
              <h5 className='fw-bold mb-4 text-dark text-start'>Your Credentials</h5>

              {/* Verify Token Display */}
              <div className='mb-4'>
                <label className='form-label fw-semibold text-gray-700 d-block text-start'>
                  Verify Token
                </label>
                <div className='d-flex align-items-center justify-content-between p-1 rounded bg-white border'>
                  <code
                    className='fs-6 text-dark me-4'
                    style={{
                      wordBreak: 'break-all',
                      backgroundColor: 'transparent',
                      boxShadow: 'none'
                    }}
                  >
                    {verifyToken}
                  </code>
                  <button
                    type='button'
                    className={`btn btn-sm btn-icon ${
                      copiedToken ? 'btn-light-success' : 'btn-light-primary'
                    }`}
                    onClick={() => handleCopy(verifyToken, 'token')}
                    title={copiedToken ? 'Copied!' : 'Copy to clipboard'}
                  >
                    <i className={`bi ${copiedToken ? 'bi-check-lg' : 'bi-clipboard'}`}></i>
                  </button>
                </div>
              </div>

              {/* Callback URL Display */}
              <div>
                <label className='form-label fw-semibold text-gray-700 d-block text-start'>
                  Callback URL
                </label>
                <div className='d-flex align-items-center justify-content-between p-3 rounded bg-white border'>
                  <code
                    className='fs-6 text-dark me-4 '
                    style={{
                      wordBreak: 'break-all',
                      backgroundColor: 'transparent',
                      boxShadow: 'none'
                    }}
                  >
                    {webhookUrl}
                  </code>
                  <button
                    type='button'
                    className={`btn btn-sm btn-icon ${
                      copiedUrl ? 'btn-light-success' : 'btn-light-primary'
                    }`}
                    onClick={() => handleCopy(webhookUrl, 'url')}
                    title={copiedUrl ? 'Copied!' : 'Copy to clipboard'}
                  >
                    <i className={`bi ${copiedUrl ? 'bi-check-lg' : 'bi-clipboard'}`}></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className='text-start mt-6 px-3'>
          <button
            type='submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={formik.isSubmitting || !formik.isValid || loadingOL}
          >
            {!loadingOL && (
              <span className='indicator-label'>{hasExistingConfig ? 'Update' : 'Submit'}</span>
            )}
            {loadingOL && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default InstagramModal
